import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getOrganisations,
  GetOrganisationsParams,
  createOrganisation,
  updateOrganisation,
  deleteOrganisation,
} from "@/services/organisation";
import type { CreateOrganisationDto, UpdateOrganisationDto } from "@/types";

export const ORGANISATIONS_QUERY_KEY = "organisations";

/**
 * Hook to fetch organisations with pagination
 */
export function useOrganisations(params?: GetOrganisationsParams) {
  return useQuery({
    queryKey: [ORGANISATIONS_QUERY_KEY, params],
    queryFn: () => getOrganisations(params),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to create an organisation
 */
export function useCreateOrganisation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateOrganisationDto) => createOrganisation(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ORGANISATIONS_QUERY_KEY] });
    },
  });
}

/**
 * Hook to update an organisation
 */
export function useUpdateOrganisation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateOrganisationDto }) =>
      updateOrganisation(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ORGANISATIONS_QUERY_KEY] });
    },
  });
}

/**
 * Hook to delete an organisation
 */
export function useDeleteOrganisation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteOrganisation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ORGANISATIONS_QUERY_KEY] });
    },
  });
}
